import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { workspaceStore } from "../lib/workspaceStore";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";

export default function WorkspacesPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const selectedId = useMemo(() => selected?.id, [selected]);

  const refresh = useCallback(async () => {
    setErr(null);
    const res = await api.get("/api/workspaces");
    const available = res.data.items || [];
    const cached = workspaceStore.getSelected();
    const allowed = available.find((item) => item.id === cached?.id) || null;
    workspaceStore.setSelected(allowed);
    setSelected(allowed);
    setItems(available);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function createWorkspace() {
    setErr(null);
    setLoading(true);
    try {
      const res = await api.post("/api/workspaces", { name });
      const ws = res.data;
      setName("");
      setOpen(false);
      await refresh();

      // Auto-select newly created workspace
      workspaceStore.setSelected(ws);
      setSelected(ws);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        "Unable to create workspace. If you're on trial/starter, you may need to upgrade to create more.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  function pick(ws) {
    workspaceStore.setSelected(ws);
    setSelected(ws);
  }

  return (
    <div className="space-y-6" data-testid="workspaces-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-2xl font-semibold">Workspaces</div>
          <div className="text-sm text-muted-foreground">
            Create a workspace for each brand/client. Your plan controls how many you can have.
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-workspace-btn">Create Workspace</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create workspace</DialogTitle>
              <DialogDescription>
                Enter a name for your new workspace. Each workspace can have its own Brand DNA and generations.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                placeholder="e.g., My Store, Client A, Dental Office"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="workspace-name-input"
              />
              {err && <div className="text-sm text-red-600">{err}</div>}
              <Button
                className="w-full"
                disabled={loading || name.trim().length < 2}
                onClick={createWorkspace}
                data-testid="create-workspace-submit"
              >
                {loading ? "Creating..." : "Create"}
              </Button>
              <div className="text-xs text-muted-foreground">
                Tip: On trial/starter you'll typically have 1 workspace.
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {err && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-700">{err}</CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Selected workspace
              {selected ? <Badge variant="secondary">Active</Badge> : <Badge variant="outline">None</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {selected ? (
              <>
                <div className="text-lg font-medium">{selected.name}</div>
                <div className="text-xs text-muted-foreground">ID: {selected.id}</div>
                <Separator className="my-3" />
                <div className="text-sm text-muted-foreground">
                  Next: Add Brand DNA, then generate ads with Maya, Cole, and Coach River.
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">
                No workspace selected. Create one or select from the list.
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>All workspaces</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {items.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No workspaces yet. Create your first workspace to begin.
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((ws) => (
                  <button
                    key={ws.id}
                    onClick={() => pick(ws)}
                    className={[
                      "w-full rounded-lg border p-3 text-left transition",
                      ws.id === selectedId ? "bg-muted" : "hover:bg-muted/60",
                    ].join(" ")}
                    data-testid={`workspace-item-${ws.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{ws.name}</div>
                      {ws.id === selectedId && <Badge variant="secondary">Selected</Badge>}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">{ws.id}</div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
